import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  Self,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { errorMessages } from '../core/errors/error-messages';

@Component({
    selector: 'app-mat-base-input',
    template: ``,
    standalone: true,
})
export class MatBaseInputComponent implements ControlValueAccessor, OnDestroy, OnChanges, MatFormFieldControl<string> {
  @Input() formControl: UntypedFormControl;
  @Input() hintLabel: string;
  @Input() label: string;
  @Input() autocomplete = 'off';
  @Input() appearance: 'fill' | 'outline' = 'outline';
  @Input() infoText: string;
  @Input() name: string;
  @Input() id: string;
  @Input() inlineLabel = false;
  @Input() placeholder: string;
  @Input() explanation: string;
  @Input() labelExplanation: string;
  @Input() explanationIcon = 'info_outline';
  @Input() hideLabel = false;
  @Input() customErrors: any;
  @Input() suffixIcon: string;
  @Input() suffixTooltip: string;
  @Input() prefixIcon: string;
  @Input() readonly = false;
  @Input() hideRequiredMarker = false;
  @Output() valueChanged = new EventEmitter<any>();
  @Output() onSuffixClicked = new EventEmitter<any>();

  private _errorState = false;
  public get errorState() {
    return this._errorState;
  }

  public set errorState(value) {
    this._errorState = value;
  }

  stateChanges = new Subject<void>();
  touched = false;
  focused = false;

  get empty() {
    const value = this.formControl?.value || this.ngControl.value;
    return !!value;
  }

  private _required = false;
  private _disabled = false;
  private _submitted = false;

  _id: string;
  errorDescriptions: {};

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    public _elementRef: ElementRef<HTMLElement>,
  ) {
    this.setId();

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  propagateChange: Function;
  onTouched: Function;

  setId(): void {
    if (this.id) {
      this._id = this.id;
    }
    if (this.name) {
      this._id = this._id = _.uniqueId(`${this.name}_`);
    }

    this._id = _.uniqueId('id_');
  }

  @Input()
  get required() {
    return this._required;
  }

  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @Input()
  get value(): string | null {
    if (!this.formControl) {
      return this.ngControl.value;
    }
    return this.formControl.value;
  }

  set value(obj: string | String | null) {
    this.stateChanges.next();
  }

  @Input()
  get submitted(): boolean {
    return this._submitted;
  }

  set submitted(value: boolean) {
    this._submitted = coerceBooleanProperty(value);
    console.log('this._submitted', this._submitted, this._disabled);
    if (this.formControl) {
      if (this._submitted) {
        this.formControl.disable();
      } else {
        this._disabled ? this.formControl.disable() : this.formControl.enable();
      }
    }
    if (this.submitted) {
      if (this.onTouched) {
        this.onTouched();
      }
    }
    console.log('form disabled', this.formControl?.disabled);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    console.log('set disabled', this._disabled);
    if (this.formControl) {
      this._disabled ? this.formControl.disable() : this.formControl.enable();
    }
    this.stateChanges.next();
  }

  ngOnDestroy() {
    this.stateChanges.complete();
  }

  isUnTouched() {
    return this.formControl && this.formControl.untouched;
  }

  isValid() {
    return this.formControl && this.formControl.valid;
  }

  isDirty() {
    return this.formControl && this.formControl.dirty;
  }

  firstError(): string {
    if (this.formControl && this.formControl.errors) {
      for (const error in this.formControl.errors) {
        if (this.formControl.errors.hasOwnProperty(error)) {
          // get error description
          let message = this.errorDescriptions[error];
          if (!message) {
            message = `Unknown error: ${error}`;
          }

          this.touched = this.formControl.touched;

          console.log(`firstError - ${this.name}:`, message);

          return message;
        }
      }
    }

    return null;
  }

  writeValue(obj: any): void {
    if (obj != null && (typeof obj === 'string' || obj instanceof String)) {
      obj = obj.toString().trim();
    }
    this.value = obj;
    console.log('writeValue: ' + obj);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.formControl) {
      this.formControl = this.ngControl.control as FormControl;
    }
    this.errorDescriptions = Object.assign(errorMessages, this.customErrors || {});
  }

  onChange(event) {
    if (event != null && (typeof event === 'string' || event instanceof String)) {
      event = event.toString().trim();
    }

    this.onTouched();
    this.propagateChange(event);
    this.valueChanged.emit(event);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    console.log('setDisabledState', isDisabled);
  }

  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  onKeyUp($event: KeyboardEvent) {
    this.touched = true;
    this.onTouched();
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement.querySelector('.example-tel-input-container')!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this._elementRef.nativeElement.querySelector('input').focus();
    }
  }
}
